import { TemporaryPricePeriodEnum } from '@inter-ikea-kompis/enums';
import ProductPriceInformationSummaryTransformer from './ProductPriceInformationSummaryTransformer.js';
/**
 * Price utilities.
 */
export default class PriceUtility {
    /**
     * Check if shoppingProducts has a family price.
     *
     * @param shoppingProducts Shopping products.
     * @returns If it is family price.
     */
    static isFamilyPrice(shoppingProducts) {
        return shoppingProducts.some((shoppingProduct) => {
            const priceInformation = shoppingProduct?.product?.content?.priceInformation;
            return priceInformation
                ? ProductPriceInformationSummaryTransformer.transform(priceInformation).familyPrice
                : false;
        });
    }
    /**
     * Returns true if it is just one product.
     *
     * @param shoppingProducts Shopping products.
     * @returns True if just one product.
     */
    static isIndividualProduct(shoppingProducts) {
        return shoppingProducts.length === 1 && shoppingProducts[0].quantity === 1;
    }
    /**
     * Returns "true" if TRO (Time Restricted Offer) or family.
     *
     * @param totalPrice Price model.
     * @returns "true" if TRO or family.
     */
    static isTroOrFamily(totalPrice) {
        return totalPrice.family || Boolean(totalPrice.temporary && totalPrice.validDateInterval);
    }
    /**
     * Returns "true" if TRO (Time Restricted Offer).
     *
     * @param totalPrice Price model.
     * @returns "true" if TRO.
     */
    static isTro(totalPrice) {
        return !totalPrice.family && Boolean(totalPrice.temporary && totalPrice.validDateInterval);
    }
    /**
     * Returns "true" if family.
     *
     * @param totalPrice Price model.
     * @returns "true" if family.
     */
    static isFamily(totalPrice) {
        return totalPrice.family;
    }
    /**
     * Returns "true" if new lower price.
     *
     * @param totalPrice Price model.
     * @returns "true" if new lower price.
     */
    static isNewLowerPrice(totalPrice) {
        return totalPrice.newLowerPrice;
    }
    /**
     * Returns "true" if new lower price which has data for previous price.
     *
     * @param totalPrice Price model.
     * @returns "true" if new lower price with previous price data.
     */
    static isNewLowerPriceWithPreviousPrice(totalPrice) {
        return totalPrice.hasPreviousPrice && this.isNewLowerPrice(totalPrice);
    }
    /**
     * Returns "true" if an breath taking item.
     *
     * @param totalPrice Price model.
     * @returns "true" if breath taking item.
     */
    static isBreathTakingItem(totalPrice) {
        return totalPrice.breathTakingItem;
    }
    /**
     * The price unit.
     *
     * @param shoppingProducts Shopping products.
     * @param useMetricMeasures Metric or imperial values.
     * @returns The price unit.
     */
    static getPriceUnit(shoppingProducts, useMetricMeasures) {
        const product = shoppingProducts[0].product;
        if (!product.content || !product.content.priceUnit || !product.content.priceUnit[0]) {
            return null;
        }
        const priceUnit = product.content.priceUnit[0];
        if (useMetricMeasures) {
            const { factorMetric, textMetric } = priceUnit;
            return this.formatPriceUnit({ factor: factorMetric, text: textMetric });
        }
        else {
            const { factorImperial, textImperial } = priceUnit;
            return this.formatPriceUnit({ factor: factorImperial, text: textImperial });
        }
    }
    /**
     * Returns valid interval.
     *
     * @param totalPrice Price model.
     * @param localizedInformation Deprecated - Use translations and settings instead.
     * @param localizedInformation.translations ...
     * @param localizedInformation.priceSettings ...
     * @param localizedInformation.priceSettings.temporaryPricePeriod ...
     * @param translations The translations retrieved from translations service.
     * @param settings The settings retrieved from settings service.
     * @param settings.kompis ...
     * @param settings.kompis.priceSettings ...
     * @returns Interval.
     */
    static getValidInterval(totalPrice, 
    /** @deprecated Use translations and settings instead. */
    localizedInformation, translations, settings) {
        const priceSettings = settings?.kompis.priceSettings ?? localizedInformation?.priceSettings;
        const actualTranslations = translations ?? localizedInformation?.translations;
        if (!priceSettings || !actualTranslations) {
            throw Error('Either translations and settings parameters or localizedInformation parameter must be set.');
        }
        if (!this.isValidInterval(priceSettings, totalPrice.validDateInterval)) {
            return null;
        }
        const validFrom = totalPrice.validDateInterval.validFrom;
        const validTo = totalPrice.validDateInterval.validTo;
        const { priceInfoTemporaryPricePeriodEnd, priceInfoTemporaryPriceDisclaimer, priceInfoTemporaryPriceFullDateRange } = actualTranslations;
        if (TemporaryPricePeriodEnum.periodEnd === priceSettings.temporaryPricePeriod) {
            return [priceInfoTemporaryPricePeriodEnd, validTo, priceInfoTemporaryPriceDisclaimer].join(' ');
        }
        const replacedDateRange = priceInfoTemporaryPriceFullDateRange
            .replace('[[dateFrom]]', validFrom)
            .replace('{{dateFrom}}', validFrom)
            .replace('[[dateTo]]', validTo)
            .replace('{{dateTo}}', validTo);
        return [replacedDateRange, priceInfoTemporaryPriceDisclaimer].join(' ');
    }
    /**
     * Returns price excl tax.
     *
     * @param totalPrice Price model.
     * @returns Price excl tax.
     */
    static getPriceExclTax(totalPrice) {
        return totalPrice.familyExclTax || totalPrice.exclTax;
    }
    /**
     * Returns the product that has the lowest regular price.
     *
     * @param products The products.
     * @returns The product with the lowest price.
     */
    static getLowestRegularPrice(products) {
        const productsOrderedByPrice = products
            .map((product) => ({
            product,
            price: this.getRegularPrice(product)
        }))
            .filter(({ price }) => price !== null)
            .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
            .map(({ product }) => product);
        return productsOrderedByPrice[0];
    }
    /**
     * Returns the array of shopping products that has the lowest total regular price.
     *
     * @param shoppingProductGroups Array of arrays of shopping products.
     * @returns The array of shopping products with the lowest total regular price.
     */
    static getShoppingProductsWithLowestTotalRegularPrice(shoppingProductGroups) {
        const getTotalPrice = (shoppingProducts) => {
            if (shoppingProducts.some((shoppingProduct) => !shoppingProduct.product.valid || this.getRegularPrice(shoppingProduct.product) === null)) {
                return null;
            }
            return shoppingProducts.reduce((sum, shoppingProduct) => sum + (this.getRegularPrice(shoppingProduct.product) ?? 0) * shoppingProduct.quantity, 0);
        };
        const shoppingProductGroupsOrderedByTotalPrice = shoppingProductGroups
            .map((shoppingProducts) => ({
            shoppingProducts,
            totalPrice: getTotalPrice(shoppingProducts)
        }))
            .filter(({ totalPrice }) => totalPrice !== null)
            .sort((a, b) => {
            return (a.totalPrice ?? 0) - (b.totalPrice ?? 0);
        })
            .map(({ shoppingProducts }) => shoppingProducts);
        if (shoppingProductGroupsOrderedByTotalPrice.length === 0) {
            return null;
        }
        return shoppingProductGroupsOrderedByTotalPrice[0];
    }
    /**
     * Returns price.
     *
     * @param totalPrice Price model.
     * @returns Price.
     */
    static getPrice(totalPrice) {
        return totalPrice.familyInclTax || totalPrice.inclTax;
    }
    /**
     * Returns price.
     *
     * @param totalPrice Price model.
     * @returns Price.
     */
    static getFromPrice(totalPrice) {
        if (this.isNewLowerPrice(totalPrice)) {
            return totalPrice.inclTax;
        }
        return totalPrice.regularInclTax || totalPrice.inclTax;
    }
    /**
     * Returns currency symbol.
     *
     * @param totalPrice Price model.
     * @param localizedInformationOrSettings Either settings retrieved from settings service or localized information fetched from localization service.
     * @returns Currency symbol.
     */
    static getCurrencySymbol(totalPrice, localizedInformationOrSettings) {
        const priceSettings = 'kompis' in localizedInformationOrSettings
            ? {
                currencyCode: localizedInformationOrSettings.localisation.currencyCode,
                currencySymbol: localizedInformationOrSettings.localisation.currencySymbol,
                currencyCodeAlt: localizedInformationOrSettings.kompis.priceSettings.currencyCodeAlt,
                currencySymbolAlt: localizedInformationOrSettings.kompis.priceSettings.currencySymbolAlt
            }
            : localizedInformationOrSettings.priceSettings;
        if (totalPrice.currencyCode === priceSettings.currencyCode) {
            return priceSettings.currencySymbol;
        }
        else if (totalPrice.currencyCode === priceSettings.currencyCodeAlt &&
            priceSettings.currencySymbolAlt) {
            return priceSettings.currencySymbolAlt;
        }
        return null;
    }
    /**
     * If there is a valid interval to show.
     *
     * @param priceSettings ...
     * @param priceSettings.temporaryPricePeriod ...
     * @param totalPriceDateInterval Price date interval.
     * @returns If it is valid interval.
     */
    static isValidInterval(priceSettings, totalPriceDateInterval) {
        return (!!totalPriceDateInterval &&
            priceSettings.temporaryPricePeriod !== TemporaryPricePeriodEnum.periodNone);
    }
    /**
     * @param options Options.
     * @param options.factor Either Metric or imperial.
     * @param options.text Either Metric or imperial.
     */
    static formatPriceUnit(options) {
        const { factor, text } = options;
        if (!factor || !text) {
            return null;
        }
        const formattedFactor = factor === 1.0 ? '' : factor;
        return `${formattedFactor} ${text}`;
    }
    /**
     * @param product The product.
     */
    static getRegularPrice(product) {
        if (!product.content || !product.content.priceInformation) {
            return null;
        }
        return (product.content.priceInformation.salesPrice.find((p) => p.typeCode == 'RegularSalesUnitPrice')
            ?.priceInclTax ?? null // The salesPrice array always contains a RegularSalesUnitPrice so null should never be returned here
        );
    }
}
//# sourceMappingURL=PriceUtility.js.map